import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExpandableSection } from '@/components/ExpandableSection';
import { ReviewSearchBar } from '@/components/ReviewSearchBar';
import { useProfileSections } from '@/hooks/useProfileSections';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Loader2, 
  UserPlus, 
  UserMinus, 
  ArrowLeft, 
  BookOpen, 
  Star, 
  Heart,
  Search,
  AlertCircle,
  Sparkles,
  Activity
} from 'lucide-react';
import { DinoAvatar } from '@/components/DinoAvatar';
import { BookCard } from '@/components/BookCard';
import { OnboardingCard } from '@/components/OnboardingCard';
import { ProfileReviewRow } from '@/components/ProfileReviewRow';
import { ProfileReviewSkeleton } from '@/components/ProfileReviewSkeleton';
import { BookRow } from '@/components/BookRow';
import { BookRowSkeleton } from '@/components/BookRowSkeleton';
import { TBRRowContent } from '@/components/TBRRowContent';
import { ReadingRowContent } from '@/components/ReadingRowContent';
import { ReadRowContent } from '@/components/ReadRowContent';
import { UnfinishedRowContent } from '@/components/UnfinishedRowContent';
import { AuthorHeader } from '@/components/AuthorHeader';
import { GlobalLeaderboard } from '@/components/GlobalLeaderboard';
import { RankLaterQueue } from '@/components/RankLaterQueue';
import { UpdateReadingProgressDialog } from '@/components/UpdateReadingProgressDialog';
import { StartDinoDuoButton } from '@/components/StartDinoDuoButton';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UserActivityLog } from '@/components/UserActivityLog';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { FollowersDialog } from '@/components/FollowersDialog';
import { MetaTags } from '@/components/MetaTags';
import { ExternalShareButton } from '@/components/ExternalShareButton';
import { QuickReviewDialog } from '@/components/QuickReviewDialog';
import { MarkAsUnfinishedDialog } from '@/components/MarkAsUnfinishedDialog';
import { BlockUserButton } from '@/components/BlockUserButton';
import { ArchetypeMascot } from '@/components/ArchetypeMascot';
import { Archetype } from '@/lib/archetype';
import { CurrentlyReadingShowcase } from '@/components/CurrentlyReadingShowcase';

interface ProfileData {
  id: string;
  username: string;
  name: string;
  bio?: string;
  image?: string;
  image_thumb?: string;
  created_at: string;
  archetype?: string;
}

interface BookWithRating {
  // Book fields
  id: string;
  title: string;
  subtitle?: string;
  authors: string[];
  published_year?: number;
  cover_url?: string;
  cover_cache_url?: string;
  synopsis?: string;
  page_count?: number;
  author?: string;
  isbn10?: string;
  isbn13?: string;
  open_library_id?: string;
  source?: string;
  subjects?: string[];
  // User book fields
  user_book_id: string;
  rating_star?: number;
  shelf?: string;
  review_text?: string;
  read_at?: string;
  started_at?: string;
  finished_at?: string;
  created_at: string;
  current_page?: number;
  total_pages?: number;
  reading_progress_percent?: number;
  last_reading_date?: string;
  is_purchased?: boolean;
}

interface UserStats {
  totalBooks: number;
  readBooks: number;
  currentlyReading: number;
  toRead: number;
  averageRating: number;
  followers: number;
  following: number;
}

interface Activity {
  id: string;
  type: string;
  book_title?: string;
  book_cover_url?: string;
  created_at: string;
  payload?: any;
}

function PublicProfile() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [books, setBooks] = useState<BookWithRating[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [stats, setStats] = useState<UserStats>({
    totalBooks: 0,
    readBooks: 0,
    currentlyReading: 0,
    toRead: 0,
    averageRating: 0,
    followers: 0,
    following: 0
  });
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('library');
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState<BookWithRating | null>(null);
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [followersDialogOpen, setFollowersDialogOpen] = useState(false);
  const [followersDialogTab, setFollowersDialogTab] = useState<'followers' | 'following'>('followers');
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedBookForReview, setSelectedBookForReview] = useState<BookWithRating | null>(null);
  const [unfinishedDialogOpen, setUnfinishedDialogOpen] = useState(false);
  const [selectedBookForDNF, setSelectedBookForDNF] = useState<BookWithRating | null>(null);
  const { isAdmin } = useIsAdmin();

  // Use profile sections hook for managing expandable sections
  const {
    sectionsExpanded,
    toggleSection,
    reviewsSearch,
    setReviewsSearch,
    reviewsRatingFilter,
    setReviewsRatingFilter,
    reviewsSortBy,
    setReviewsSortBy,
    currentlyReadingBooks,
    allReviewBooks,
    reviewBooks,
    tbrBooks,
    dnfBooks,
    displayedReviews,
    displayedTbr,
    displayedDnf,
  } = useProfileSections({ books });

  useEffect(() => {
    if (username) {
      loadProfile();
    }
  }, [username, user]);

  const loadProfile = async () => {
    if (!username) return;
    
    try {
      setLoading(true);
      setError(null);

      // Load profile using secure function that respects privacy settings
      const { data: profileData, error: profileError } = await supabase
        .rpc('get_public_profile_safe', { profile_username: username });

      if (profileError) {
        console.error('Profile load error:', profileError);
        setError('Failed to load profile');
        return;
      }

      // Convert array response to single object
      const profile = profileData && profileData.length > 0 ? profileData[0] : null;

      if (!profile) {
        setProfile(null);
        setLoading(false);
        return;
      }

      setProfile(profile);

      // Load user's books - show all books if viewing own profile, only public books otherwise
      const isOwnProfile = user && user.id === profile.id;
      let booksQuery = supabase
        .from('user_books')
        .select(`
          id,
          rating_star,
          shelf,
          review_text,
          read_at,
          started_at,
          finished_at,
          created_at,
          book_id,
          current_page,
          total_pages,
          reading_progress_percent,
          last_reading_date,
          is_purchased
        `)
        .eq('user_id', profile.id);
      
      // Only filter by privacy if viewing someone else's profile
      if (!isOwnProfile) {
        booksQuery = booksQuery.eq('privacy', 'public');
      }
      
      const { data: booksData, error: booksError } = await booksQuery.order('created_at', { ascending: false });

      if (booksError) {
        console.error('Books load error:', booksError);
        setBooks([]);
      } else if (booksData) {
        // Load book details separately for each user book
        const bookIds = booksData.map(item => item.book_id).filter(Boolean);
        
        if (bookIds.length > 0) {
          const { data: bookDetails, error: bookDetailsError } = await supabase
            .from('books')
            .select('*')
            .in('id', bookIds);
          
          if (bookDetailsError) {
            console.error('Book details error:', bookDetailsError);
            setBooks([]);
          } else {
            // Merge book details with user book data
            const mergedBooks = booksData.map(userBook => {
              const bookDetail = bookDetails?.find(book => book.id === userBook.book_id);
              if (!bookDetail) return null;
              
              return {
                ...bookDetail,
                user_book_id: userBook.id,
                rating_star: userBook.rating_star,
                shelf: userBook.shelf,
                review_text: userBook.review_text,
                read_at: userBook.read_at,
                started_at: userBook.started_at,
                finished_at: userBook.finished_at,
                created_at: userBook.created_at,
                current_page: userBook.current_page,
                total_pages: userBook.total_pages,
                reading_progress_percent: userBook.reading_progress_percent,
                last_reading_date: userBook.last_reading_date,
                is_purchased: userBook.is_purchased
              } as BookWithRating;
            }).filter(book => book !== null) as BookWithRating[];
            
            setBooks(mergedBooks);
          }
        } else {
          setBooks([]);
        }
      } else {
        setBooks([]);
      }

      // Calculate stats
      const totalBooks = booksData?.length || 0;
      const readBooks = booksData?.filter(b => b.shelf === 'read').length || 0;
      const currentlyReading = booksData?.filter(b => b.shelf === 'reading').length || 0;
      const toRead = booksData?.filter(b => b.shelf === 'to-read').length || 0;
      const ratingsSum = booksData?.filter(b => b.rating_star).reduce((sum, b) => sum + (b.rating_star || 0), 0) || 0;
      const ratingsCount = booksData?.filter(b => b.rating_star).length || 0;
      const averageRating = ratingsCount > 0 ? ratingsSum / ratingsCount : 0;

      // Load follower/following counts
      const [followersResult, followingResult] = await Promise.all([
        supabase.from('follows').select('id').eq('following_id', profile.id),
        supabase.from('follows').select('id').eq('follower_id', profile.id)
      ]);

      setStats({
        totalBooks,
        readBooks,
        currentlyReading,
        toRead,
        averageRating,
        followers: followersResult.data?.length || 0,
        following: followingResult.data?.length || 0
      });

      // Check if current user is following this profile
      if (user && user.id !== profile.id) {
        const { data: followData } = await supabase
          .from('follows')
          .select('id')
          .eq('follower_id', user.id)
          .eq('following_id', profile.id)
          .maybeSingle();
        
        setIsFollowing(!!followData);
      }

      // Load activities (if following or own profile)
      if (user && (user.id === profile.id || isFollowing)) {
        const { data: activitiesData } = await supabase
          .from('activities')
          .select('*')
          .eq('user_id', profile.id)
          .order('created_at', { ascending: false })
          .limit(10);
          
        setActivities(activitiesData || []);
      }

    } catch (error) {
      console.error('Unexpected error loading profile:', error);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleFollowToggle = async () => {
    if (!user || !profile) return;

    try {
      if (isFollowing) {
        // Unfollow
        await supabase
          .from('follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', profile.id);
        
        setIsFollowing(false);
        setStats(prev => ({ ...prev, followers: prev.followers - 1 }));
        
        toast({
          title: "Unfollowed",
          description: `You are no longer following ${profile.name}.`
        });
      } else {
        // Follow
        await supabase
          .from('follows')
          .insert({
            follower_id: user.id,
            following_id: profile.id
          });
        
        setIsFollowing(true);
        setStats(prev => ({ ...prev, followers: prev.followers + 1 }));
        
        toast({
          title: "Following",
          description: `You are now following ${profile.name}.`
        });
      }
    } catch (error) {
      console.error('Follow toggle error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update follow status. Please try again."
      });
    }
  };

  const getBooksByShelf = (shelf: string) => {
    return books.filter(book => book.shelf === shelf);
  };

  const handleOpenProgressDialog = (book: BookWithRating) => {
    setSelectedBook(book);
    setProgressDialogOpen(true);
  };

  const handleProgressUpdated = () => {
    // Reload profile data to get updated progress
    loadProfile();
  };

  const handleStartReading = async (bookId: string) => {
    if (!user || !profile) return;

    try {
      const book = books.find(b => b.id === bookId);
      const { error } = await supabase
        .from('user_books')
        .update({
          shelf: 'reading',
          started_at: new Date().toISOString().split('T')[0],
          total_pages: book?.page_count || null,
          current_page: 0,
        })
        .eq('user_id', user.id)
        .eq('book_id', bookId);

      if (error) throw error;

      toast({
        title: "Started reading",
        description: "Book moved to Currently Reading.",
      });

      loadProfile();
    } catch (error) {
      console.error('Error starting book:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not update reading status.",
      });
    }
  };

  const handleRemoveFromTBR = async (bookId: string) => {
    if (!user || !profile) return;

    try {
      const { error } = await supabase
        .from('user_books')
        .delete()
        .eq('user_id', user.id)
        .eq('book_id', bookId)
        .eq('shelf', 'to-read');

      if (error) throw error;

      toast({
        title: "Removed from TBR",
        description: "Book removed from your To Be Read list.",
      });

      loadProfile();
    } catch (error) {
      console.error('Error removing from TBR:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not remove book from TBR.",
      });
    }
  };

  const handleTogglePurchased = async (bookId: string, isPurchased: boolean) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('user_books')
        .update({ is_purchased: isPurchased })
        .eq('user_id', user.id)
        .eq('book_id', bookId);
      
      if (error) throw error;
      
      // Update local state
      setBooks(prevBooks =>
        prevBooks.map(book =>
          book.id === bookId 
            ? { ...book, is_purchased: isPurchased }
            : book
        )
      );
      
      toast({
        title: isPurchased ? "Marked as Owned" : "Marked as Not Purchased",
        description: isPurchased 
          ? "Book is now tracked as purchased/owned" 
          : "Book is now tracked as not purchased",
      });
    } catch (error) {
      console.error('Error updating purchased status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not update purchased status.",
      });
    }
  };

  const handleReviewUpdate = async (
    bookId: string, 
    newReviewText: string, 
    newRating?: number,
    startedAt?: Date | null,
    finishedAt?: Date | null
  ) => {
    try {
      const updateData: any = { 
        user_id: profile!.id,
        book_id: bookId,
        review_text: newReviewText 
      };
      if (newRating !== undefined) {
        updateData.rating_star = newRating;
      }
      if (startedAt !== undefined) {
        updateData.started_at = startedAt ? startedAt.toISOString().split('T')[0] : null;
      }
      if (finishedAt !== undefined) {
        updateData.finished_at = finishedAt ? finishedAt.toISOString().split('T')[0] : null;
      }

      // Try update first; if no row updated, insert
      const { data: updatedRows, error: updateErr } = await supabase
        .from('user_books')
        .update(updateData)
        .eq('user_id', profile!.id)
        .eq('book_id', bookId)
        .select('id');

      // Check for errors first
      if (updateErr) {
        throw updateErr;
      }

      // If no error but no rows updated, try insert
      if (!updatedRows || updatedRows.length === 0) {
        const { error: insertErr } = await supabase
          .from('user_books')
          .insert(updateData);
        if (insertErr) throw insertErr;
      }

      // Update or insert completed

      // Update local state
      setBooks(prev => prev.map(book => 
        book.id === bookId 
          ? { 
              ...book, 
              review_text: newReviewText,
              ...(newRating !== undefined && { rating_star: newRating }),
              ...(startedAt !== undefined && { started_at: updateData.started_at }),
              ...(finishedAt !== undefined && { finished_at: updateData.finished_at })
            }
          : book
      ));

      toast({
        title: "Review updated",
        description: "Your review has been successfully updated."
      });
    } catch (error) {
      console.error('Error updating review:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update review. Please try again."
      });
      throw error;
    }
  };

  const handleBookRemove = async (bookId: string) => {
    try {
      const { error } = await supabase
        .from('user_books')
        .delete()
        .eq('user_id', profile!.id)
        .eq('book_id', bookId);

      if (error) throw error;

      // Update local state - completely remove the book
      setBooks(prev => prev.filter(book => book.id !== bookId));

      toast({
        title: "Book removed",
        description: "The book has been completely removed from your profile."
      });
    } catch (error) {
      console.error('Error removing book:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to remove book. Please try again."
      });
      throw error;
    }
  };

  const handleOpenReview = (book: BookWithRating) => {
    setSelectedBookForReview(book);
    setReviewDialogOpen(true);
  };

  const handleReviewSubmit = async (data: {
    rating: number;
    reviewText?: string;
    finishedAt?: Date | null;
    startedAt?: Date | null;
    tags?: string[];
  }) => {
    if (!user || !profile || !selectedBookForReview) return;

    try {
      const updateData: any = {
        shelf: 'read',
        rating_star: data.rating,
        review_text: data.reviewText || '',
        tags: data.tags || [],
        finished_at: data.finishedAt 
          ? data.finishedAt.toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
      };

      if (data.startedAt) {
        updateData.started_at = data.startedAt.toISOString().split('T')[0];
      }

      // Try to update existing record
      const { data: updatedRows, error: updateError } = await supabase
        .from('user_books')
        .update(updateData)
        .eq('user_id', user.id)
        .eq('book_id', selectedBookForReview.id)
        .select('id');

      if (updateError) throw updateError;

      // If no rows were updated, insert a new record
      if (!updatedRows || updatedRows.length === 0) {
        const { error: insertError } = await supabase
          .from('user_books')
          .insert({
            user_id: user.id,
            book_id: selectedBookForReview.id,
            ...updateData
          });

        if (insertError) throw insertError;
      }

      // Trigger pairwise comparison if rating provided (non-blocking)
      if (data.rating > 0) {
        supabase.functions.invoke('on-rating-saved', {
          body: {
            userId: user.id,
            bookId: selectedBookForReview.id,
            rating: data.rating
          }
        }).catch(err => {
          console.error('Ranking algorithm error (non-critical):', err);
        });
      }

      toast({
        title: "Marked as read",
        description: "Book moved to Read shelf.",
      });

      setReviewDialogOpen(false);
      setSelectedBookForReview(null);
      loadProfile();
    } catch (error) {
      console.error('Error marking as read:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not update book status.",
      });
    }
  };

  const handleOpenDNF = (book: BookWithRating) => {
    setSelectedBookForDNF(book);
    setUnfinishedDialogOpen(true);
  };

  const handleConfirmDNF = async (data: { currentPage?: number; reason: string }) => {
    if (!user || !profile || !selectedBookForDNF) return;

    try {
      const totalPages = selectedBookForDNF.total_pages || selectedBookForDNF.page_count;
      const readingProgressPercent = 
        data.currentPage && totalPages 
          ? Math.round((data.currentPage / totalPages) * 100)
          : undefined;

      const updateData = {
        shelf: 'unfinished',
        current_page: data.currentPage,
        reading_progress_percent: readingProgressPercent,
        review_text: data.reason,
      };

      // Try to update existing record
      const { data: updatedRows, error: updateError } = await supabase
        .from('user_books')
        .update(updateData)
        .eq('user_id', user.id)
        .eq('book_id', selectedBookForDNF.id)
        .select('id');

      if (updateError) throw updateError;

      // If no rows were updated, insert a new record
      if (!updatedRows || updatedRows.length === 0) {
        const { error: insertError } = await supabase
          .from('user_books')
          .insert({
            user_id: user.id,
            book_id: selectedBookForDNF.id,
            ...updateData
          });

        if (insertError) throw insertError;
      }

      toast({
        title: "Marked as unfinished",
        description: "Book moved to Did Not Finish shelf.",
      });

      setUnfinishedDialogOpen(false);
      setSelectedBookForDNF(null);
      loadProfile();
    } catch (error) {
      console.error('Error marking as unfinished:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not update book status.",
      });
    }
  };

  const handleMarkAsRead = async (bookId: string) => {
    if (!user || !profile) return;

    try {
      const { error } = await supabase
        .from('user_books')
        .update({
          shelf: 'read',
          finished_at: new Date().toISOString().split('T')[0],
        })
        .eq('user_id', user.id)
        .eq('book_id', bookId);

      if (error) throw error;

      toast({
        title: "Marked as read",
        description: "Book moved to Read shelf.",
      });

      loadProfile();
    } catch (error) {
      console.error('Error marking as read:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not update book status.",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-lg">Loading profile...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto space-y-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)}
              className="mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
            <Button onClick={loadProfile} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto space-y-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)}
              className="mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Card className="card-elevated">
              <CardContent className="py-12 text-center space-y-4">
                <div className="w-16 h-16 bg-muted rounded-full mx-auto flex items-center justify-center">
                  <Search className="w-8 h-8 text-muted-foreground" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">User not found</h2>
                  <p className="text-muted-foreground">
                    The user @{username} doesn't exist or has been removed.
                  </p>
                </div>
                <div className="flex gap-4 justify-center">
                  <Button onClick={() => navigate('/')}>
                    Go Home
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/search?tab=people">
                      Search People
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const isOwnProfile = user?.id === profile.id;

  return (
    <div className="min-h-screen bg-background">
      {/* Dynamic Meta Tags for Social Sharing */}
      <MetaTags
        title={`${profile.name} (@${profile.username}) - Dino`}
        description={`${profile.name} has read ${stats.readBooks} books with an average rating of ${stats.averageRating.toFixed(1)}⭐. Join them on Dino!`}
        image={profile.image}
        url={`${window.location.origin}/u/${profile.username}`}
        type="profile"
      />

      {/* Header */}
      <header className="border-b border-border bg-muted/30">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            {isOwnProfile && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/settings/profile')}
                >
                  Edit Profile
                </Button>
                {(isAdmin || import.meta.env.DEV) && (
                  <Button
                    variant="outline"
                    onClick={() => setActivityLogOpen(true)}
                  >
                    <Activity className="w-4 h-4 mr-2" />
                    All Activity
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
            <DinoAvatar
              src={profile.image}
              name={profile.name}
              size="xl"
              className="ring-2 ring-primary/20"
            />
            
            <div className="flex-1 w-full space-y-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl sm:text-3xl font-bold">{profile.name}</h1>
                  {profile.archetype && (
                    <ArchetypeMascot archetype={profile.archetype as Archetype} size="sm" />
                  )}
                </div>
                <p className="text-lg sm:text-xl text-muted-foreground">@{profile.username}</p>
                {profile.bio && (
                  <p className="mt-2 text-sm sm:text-base text-foreground/80">{profile.bio}</p>
                )}
              </div>

              {/* Stats - Responsive Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <div className="flex flex-col items-center sm:items-start p-3 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="text-xl sm:text-2xl font-bold">{stats.readBooks}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Books</div>
                </div>
                <div className="flex flex-col items-center sm:items-start p-3 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="text-xl sm:text-2xl font-bold">{stats.averageRating.toFixed(1)}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Avg Rating</div>
                </div>
                <button 
                  className="flex flex-col items-center sm:items-start p-3 rounded-lg hover:bg-accent transition-colors"
                  onClick={() => {
                    setFollowersDialogTab('followers');
                    setFollowersDialogOpen(true);
                  }}
                >
                  <div className="text-xl sm:text-2xl font-bold">{stats.followers}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Followers</div>
                </button>
                <button 
                  className="flex flex-col items-center sm:items-start p-3 rounded-lg hover:bg-accent transition-colors"
                  onClick={() => {
                    setFollowersDialogTab('following');
                    setFollowersDialogOpen(true);
                  }}
                >
                  <div className="text-xl sm:text-2xl font-bold">{stats.following}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Following</div>
                </button>
              </div>

              {/* Follow button */}
              {user && !isOwnProfile && (
                <div className="flex gap-2">
                  <Button
                    onClick={handleFollowToggle}
                    variant={isFollowing ? "outline" : "default"}
                    className="flex items-center gap-2"
                  >
                    {isFollowing ? (
                      <>
                        <UserMinus className="w-4 h-4" />
                        Unfollow
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4" />
                        Follow
                      </>
                    )}
                  </Button>
                  <StartDinoDuoButton friendId={profile.id} />
                  <BlockUserButton
                    userId={profile.id}
                    username={profile.username}
                    variant="outline"
                    showLabel={true}
                  />
                  <ExternalShareButton
                    shareType="profile"
                    data={{
                      username: profile.username,
                      name: profile.name,
                    }}
                    variant="outline"
                    size="default"
                  />
                </div>
              )}

              {/* Share button for own profile */}
              {isOwnProfile && (
                <ExternalShareButton
                  shareType="profile"
                  data={{
                    username: profile.username,
                    name: profile.name,
                  }}
                  variant="outline"
                  size="default"
                />
              )}
            </div>
          </div>
        </div>
      </header>

      <main>
        {/* Onboarding Card - only show on own profile */}
        {isOwnProfile && (
          <div className="container mx-auto px-4 pt-8">
            <OnboardingCard />
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="border-b border-border bg-muted/30">
            <div className="container mx-auto px-4 py-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="library">Library</TabsTrigger>
                <TabsTrigger value="rankings">Rankings</TabsTrigger>
              </TabsList>
            </div>
          </div>

          <TabsContent value="library" className="container mx-auto px-4 py-8 space-y-6">
            {/* Currently Reading Showcase - Show first book prominently */}
            {currentlyReadingBooks.length > 0 && (
              <CurrentlyReadingShowcase
                book={{
                  id: currentlyReadingBooks[0].id,
                  title: currentlyReadingBooks[0].title,
                  authors: currentlyReadingBooks[0].authors || [],
                  cover_url: currentlyReadingBooks[0].cover_url,
                  cover_cache_url: currentlyReadingBooks[0].cover_cache_url,
                  current_page: currentlyReadingBooks[0].current_page,
                  total_pages: currentlyReadingBooks[0].total_pages || currentlyReadingBooks[0].page_count,
                  reading_progress_percent: currentlyReadingBooks[0].reading_progress_percent,
                  started_at: currentlyReadingBooks[0].started_at,
                }}
                isOwnProfile={isOwnProfile}
                onUpdateProgress={() => handleOpenProgressDialog(currentlyReadingBooks[0])}
                onMarkAsRead={() => handleOpenReview(currentlyReadingBooks[0])}
                onMarkAsUnfinished={() => handleOpenDNF(currentlyReadingBooks[0])}
              />
            )}

            {/* Currently Reading Section - All books list */}
            {currentlyReadingBooks.length > 1 && (
              <ExpandableSection
                title={`All Currently Reading (${currentlyReadingBooks.length})`}
                icon={<BookOpen className="w-5 h-5" />}
                count={currentlyReadingBooks.length}
                isExpanded={sectionsExpanded.currentlyReading}
                onToggle={() => toggleSection('currentlyReading')}
              >
              <div className="space-y-0">
                {currentlyReadingBooks.map((book) => (
                  <BookRow
                    key={book.id}
                    bookId={book.id}
                    title={book.title}
                    author={book.authors?.[0] || book.author}
                    isbn13={book.isbn13}
                    isbn10={book.isbn10}
                    olid={book.open_library_id}
                    coverUrl={book.cover_url}
                    coverCacheUrl={book.cover_cache_url}
                    size="md"
                    right={
                      <ReadingRowContent
                        noteText={book.review_text}
                        startedAt={book.started_at}
                        progress={book.reading_progress_percent || 0}
                        currentPage={book.current_page}
                        totalPages={book.total_pages || book.page_count}
                        lastUpdated={book.last_reading_date || book.created_at}
                        onMarkAsRead={isOwnProfile ? () => handleOpenReview(book) : undefined}
                        onUpdateProgress={isOwnProfile ? () => handleOpenProgressDialog(book) : undefined}
                        onMarkAsUnfinished={isOwnProfile ? () => handleOpenDNF(book) : undefined}
                        loading={false}
                      />
                    }
                  />
                ))}
              </div>
              </ExpandableSection>
            )}

            {/* Reviews Section - With search and filtering */}
            <ExpandableSection
              title="Reviews"
              icon={<Star className="w-5 h-5" />}
              count={allReviewBooks.length}
              isExpanded={sectionsExpanded.reviews}
              onToggle={() => toggleSection('reviews')}
              headerActions={
                allReviewBooks.length > 0 && (
                  <div className="flex-1 max-w-2xl">
                    <ReviewSearchBar
                      searchQuery={reviewsSearch}
                      onSearchChange={setReviewsSearch}
                      ratingFilter={reviewsRatingFilter}
                      onRatingFilterChange={setReviewsRatingFilter}
                      sortBy={reviewsSortBy}
                      onSortChange={setReviewsSortBy}
                    />
                  </div>
                )
              }
            >
              <div className="space-y-0">
                {displayedReviews.map((book) => (
                  <BookRow
                    key={book.id}
                    bookId={book.id}
                    title={book.title}
                    author={book.authors?.[0] || book.author}
                    isbn13={book.isbn13}
                    isbn10={book.isbn10}
                    olid={book.open_library_id}
                    coverUrl={book.cover_url}
                    coverCacheUrl={book.cover_cache_url}
                    size="md"
                    right={
                      <ReadRowContent
                        rating={book.rating_star}
                        reviewText={book.review_text}
                        startedAt={book.started_at}
                        finishedAt={book.finished_at}
                        readAt={book.read_at}
                        bookId={book.id}
                        isOwnProfile={isOwnProfile}
                        onReviewUpdate={handleReviewUpdate}
                        onBookRemove={handleBookRemove}
                      />
                    }
                  />
                ))}
              </div>
            </ExpandableSection>

            {/* TBR / Want to Read Section */}
            <ExpandableSection
              title="Want to Read"
              icon={<Heart className="w-5 h-5" />}
              count={tbrBooks.length}
              isExpanded={sectionsExpanded.tbr}
              onToggle={() => toggleSection('tbr')}
            >
              <div className="space-y-0">
                {displayedTbr.map((book) => (
                  <BookRow
                    key={book.id}
                    bookId={book.id}
                    title={book.title}
                    author={book.authors?.[0] || book.author}
                    isbn13={book.isbn13}
                    isbn10={book.isbn10}
                    olid={book.open_library_id}
                    coverUrl={book.cover_url}
                    coverCacheUrl={book.cover_cache_url}
                    size="md"
                    right={
                      <TBRRowContent
                        noteText={book.review_text}
                        addedAt={book.created_at}
                        priority={undefined}
                        isPurchased={book.is_purchased}
                        onMarkAsReading={isOwnProfile ? () => handleStartReading(book.id) : undefined}
                        onRemove={isOwnProfile ? () => handleRemoveFromTBR(book.id) : undefined}
                        onTogglePurchased={isOwnProfile ? (purchased) => handleTogglePurchased(book.id, purchased) : undefined}
                        loading={false}
                      />
                    }
                  />
                ))}
              </div>
            </ExpandableSection>

            {/* DNF / Did Not Finish Section */}
            <ExpandableSection
              title="Did Not Finish"
              icon={<AlertCircle className="w-5 h-5" />}
              count={dnfBooks.length}
              isExpanded={sectionsExpanded.dnf}
              onToggle={() => toggleSection('dnf')}
            >
              <div className="space-y-0">
                {displayedDnf.map((book) => (
                  <BookRow
                    key={book.id}
                    bookId={book.id}
                    title={book.title}
                    author={book.authors?.[0] || book.author}
                    isbn13={book.isbn13}
                    isbn10={book.isbn10}
                    olid={book.open_library_id}
                    coverUrl={book.cover_url}
                    coverCacheUrl={book.cover_cache_url}
                    size="md"
                    right={
                      <UnfinishedRowContent
                        reasonText={book.review_text}
                        startedAt={book.started_at}
                        progress={book.reading_progress_percent || 0}
                        currentPage={book.current_page}
                        totalPages={book.total_pages || book.page_count}
                        onMarkAsReading={isOwnProfile ? () => handleStartReading(book.id) : undefined}
                        onMarkAsRead={isOwnProfile ? () => handleMarkAsRead(book.id) : undefined}
                        onRemove={isOwnProfile ? () => handleBookRemove(book.id) : undefined}
                        onReasonUpdate={isOwnProfile ? (newReason) => handleReviewUpdate(book.id, newReason) : undefined}
                        loading={false}
                      />
                    }
                  />
                ))}
              </div>
            </ExpandableSection>
          </TabsContent>

          <TabsContent value="rankings" className="container mx-auto px-4 py-8 space-y-6">
            {isOwnProfile && <RankLaterQueue />}
            <GlobalLeaderboard userId={profile.id} isOwnProfile={isOwnProfile} />
          </TabsContent>
        </Tabs>

        {/* Update Reading Progress Dialog */}
        {selectedBook && (
          <UpdateReadingProgressDialog
            open={progressDialogOpen}
            onOpenChange={setProgressDialogOpen}
            bookId={selectedBook.id}
            userBookId={selectedBook.user_book_id}
            currentPage={selectedBook.current_page || 0}
            totalPages={selectedBook.total_pages || selectedBook.page_count || 0}
            bookTitle={selectedBook.title}
            onProgressUpdated={handleProgressUpdated}
          />
        )}

        {/* Quick Review Dialog */}
        {selectedBookForReview && (
          <QuickReviewDialog
            isOpen={reviewDialogOpen}
            onClose={() => {
              setReviewDialogOpen(false);
              setSelectedBookForReview(null);
            }}
            book={{
              id: selectedBookForReview.id,
              title: selectedBookForReview.title,
              author: selectedBookForReview.authors?.[0] || selectedBookForReview.author || 'Unknown Author',
            }}
            onSubmit={handleReviewSubmit}
            initialData={{
              rating: selectedBookForReview.rating_star,
              reviewText: selectedBookForReview.review_text,
              startedAt: selectedBookForReview.started_at,
            }}
          />
        )}

        {/* Mark as Unfinished Dialog */}
        {selectedBookForDNF && (
          <MarkAsUnfinishedDialog
            open={unfinishedDialogOpen}
            onOpenChange={(open) => {
              setUnfinishedDialogOpen(open);
              if (!open) setSelectedBookForDNF(null);
            }}
            onConfirm={handleConfirmDNF}
            bookTitle={selectedBookForDNF.title}
            totalPages={selectedBookForDNF.total_pages || selectedBookForDNF.page_count}
            currentPage={selectedBookForDNF.current_page}
          />
        )}

        {/* All Activity Dialog */}
        <Dialog open={activityLogOpen} onOpenChange={setActivityLogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>All Activity</DialogTitle>
            </DialogHeader>
            {profile?.id && <UserActivityLog userId={profile.id} />}
          </DialogContent>
        </Dialog>

        <FollowersDialog
          open={followersDialogOpen}
          onOpenChange={setFollowersDialogOpen}
          userId={profile.id}
          defaultTab={followersDialogTab}
        />

        {/* Dev diagnostics */}
        {import.meta.env.DEV && (
          <Card className="mt-8 bg-muted/50">
            <CardHeader>
              <CardTitle className="text-sm">Dev Diagnostics</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              <p>Profile ID: {profile.id}</p>
              <p>Username: {profile.username}</p>
              <p>Is Own Profile: {isOwnProfile ? 'Yes' : 'No'}</p>
              <p>Is Following: {isFollowing ? 'Yes' : 'No'}</p>
              <p>Books Loaded: {books.length}</p>
              <p>Last Error: {error || 'None'}</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

export default PublicProfile;